import { useState, useEffect, FormEvent } from 'react';
import { Project } from '@/types';
import { projectGroupsApi, usersApi } from '@/services/api';

// Build a display label for a user — name if set, otherwise email
const userDisplayName = (u: { firstName?: string | null; lastName?: string | null; email: string }) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email;

const EditProjectForm = ({
  project,
  onSave,
  onCancel
}: {
  project: Project;
  onSave: (data: any) => void;
  onCancel: () => void;
}) => {
  const [formData, setFormData] = useState({
    name: project.name,
    status: project.status,
    totalTarget: project.totalTarget ?? '',
    notes: project.notes || '',
    allowIdReuse: project.allowIdReuse,
    debugRedirects: project.debugRedirects || false,
    linksKilled: project.linksKilled ?? false,
    projectManagerId: project.projectManagerId || '',
  });

  // CR-03: project group selection ('' = none, or a group id). Groups are created
  // from the "New Group" button on the Projects page.
  const [groups, setGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [groupSelection, setGroupSelection] = useState<string>(project.projectGroupId || '');

  // Users for the Project Manager select
  const [users, setUsers] = useState<Array<{ id: string; email: string; role: string; firstName?: string | null; lastName?: string | null }>>([]);

  useEffect(() => {
    projectGroupsApi.getAll()
      .then(res => { if (res.success && res.data) setGroups(res.data); })
      .catch(err => console.error('Failed to load project groups:', err));
    usersApi.getSelectable()
      .then(res => { if (res.success && res.data) setUsers(res.data); })
      .catch(err => console.error('Failed to load users:', err));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // ResearchDefender is no longer a project-level setting — it's managed per
    // collection — so this form never sends RD fields.
    onSave({
      ...formData,
      // empty string means "no target" — send null to clear it
      totalTarget: formData.totalTarget === '' ? null : Number(formData.totalTarget),
      projectGroupId: groupSelection || null,
      // empty string means "Unassigned" — send null so it passes uuid validation
      projectManagerId: formData.projectManagerId || null,
    });
  };

  return (
    <form id="project-edit-form" onSubmit={handleSubmit} className="contents">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">Project Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="input"
              required
            />
          </div>
          
                  
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">Status *</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="input"
              required
            >
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">Total Target</label>
            <input
              type="number"
              name="totalTarget"
              min="0"
              step="1"
              value={formData.totalTarget}
              onChange={handleChange}
              className="input"
              placeholder="e.g. 500"
            />
            <p className="text-xs text-gray-500 mt-1">Overall completes goal for this project. Used as the denominator for the completes progress chart in Advanced Analytics. Leave blank to fall back to the sum of collection targets.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">Project Group</label>
            <select
              value={groupSelection}
              onChange={(e) => setGroupSelection(e.target.value)}
              className="input"
            >
              <option value="">No group</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Projects in the same group share cross-project duplicate detection. Create groups with the "New Group" button on the Projects page.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">Project Manager</label>
            <select
              name="projectManagerId"
              value={formData.projectManagerId}
              onChange={handleChange}
              className="input"
            >
              <option value="">Unassigned</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{userDisplayName(u)}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">The user responsible for this project.</p>
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="allowIdReuse"
                checked={formData.allowIdReuse}
                onChange={handleChange}
                className="rounded border-gray-300"
              />
              <span className="text-sm font-medium">Allow ID Reuse</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">Allow multiple uses of the same ID</p>
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="debugRedirects"
                checked={formData.debugRedirects}
                onChange={handleChange}
                className="rounded border-gray-300"
              />
              <span className="text-sm font-medium">Enable Debug Mode</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">Show debug pages with redirect URLs instead of redirecting on terminal endpoints</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">Kill Links</label>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, linksKilled: !prev.linksKilled }))}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                formData.linksKilled
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${formData.linksKilled ? 'bg-red-600' : 'bg-green-600'}`} />
              {formData.linksKilled ? 'LINKS KILLED' : 'LINKS ACTIVE'}
            </button>
            <p className="text-xs text-gray-500 mt-1">When killed, all survey traffic returns silently — no tracking, no redirects, no logging.</p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-500 mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              className="input w-full"
              placeholder="Add any project notes, special instructions, or important information here..."
            />
            <p className="text-xs text-gray-500 mt-1">Optional: Add notes or special instructions for this project</p>
          </div>

                  </div>
      </div>
    </form>
  );
};

export default EditProjectForm;